import { API_BASE } from './rpc';

interface ClientTelemetryPayload {
  service: string;
  errorName?: string;
  message: string;
  stack?: string;
  route?: string;
  statusCode?: number;
  level?: 'error' | 'warn' | 'info';
  customDetails?: any;
}

// Client-side rate-limiting and deduplication cache
const recentErrorSignatures = new Map<string, number>();
const DEDUPE_WINDOW_MS = 60_000;
let clientReportsThisMinute = 0;
let clientWindowStart = Date.now();
const MAX_REPORTS_PER_MINUTE = 5;

// Noise filtering regexes
const IGNORED_ERROR_PATTERNS = [
  /ResizeObserver loop completed with undelivered notifications/i,
  /ResizeObserver loop limit exceeded/i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-extension:\/\//i,
  /Loading chunk [0-9]+ failed/i, // Benign transient network chunk errors
];

export function reportClientDiagnosticError(payload: ClientTelemetryPayload): void {
  try {
    if (typeof window === 'undefined') return;

    const message = payload.message || 'Unknown Client Error';
    const stack = payload.stack || '';

    // Ignore benign or browser extension errors
    for (const pattern of IGNORED_ERROR_PATTERNS) {
      if (pattern.test(message) || pattern.test(stack)) {
        return;
      }
    }

    const now = Date.now();
    if (now - clientWindowStart > 60_000) {
      clientWindowStart = now;
      clientReportsThisMinute = 0;
    }

    if (clientReportsThisMinute >= MAX_REPORTS_PER_MINUTE) {
      console.warn('[Telemetry Throttled] Client report cap reached for this minute.');
      return;
    }

    const route = payload.route || (window.location.pathname + window.location.search);
    const signature = `${payload.service}:${payload.errorName || 'Error'}:${route}:${message.substring(0, 100)}`;
    const lastReported = recentErrorSignatures.get(signature);

    if (lastReported && now - lastReported < DEDUPE_WINDOW_MS) {
      return; // Deduplicate repeated client error within 60s
    }

    recentErrorSignatures.set(signature, now);
    clientReportsThisMinute++;

    // Prune stale signatures
    if (recentErrorSignatures.size > 50) {
      for (const [key, ts] of recentErrorSignatures.entries()) {
        if (now - ts > DEDUPE_WINDOW_MS * 2) {
          recentErrorSignatures.delete(key);
        }
      }
    }

    const endpoint = `${API_BASE.replace(/\/$/, '')}/api/diagnostics/report`;
    const body = JSON.stringify({
      service: payload.service,
      errorName: payload.errorName || 'ClientError',
      message,
      stack,
      route,
      statusCode: payload.statusCode || 0,
      level: payload.level || 'error',
      userAgent: navigator.userAgent,
      customDetails: {
        ...payload.customDetails,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        devicePixelRatio: window.devicePixelRatio,
        url: window.location.href,
      },
    });

    if (typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      const sent = navigator.sendBeacon(endpoint, blob);
      if (sent) return;
    }

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      mode: 'cors',
    }).catch(() => {});
  } catch (err) {
    // Fail silent, never disrupt application execution
  }
}

let initialized = false;

export function initClientTelemetry(service: 'dashboard' | 'crm'): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  window.addEventListener('error', (event) => {
    // Filter out resource load errors (e.g. <img> failing to load)
    if (!event.error && event.target && (event.target as HTMLElement).tagName) {
      return;
    }
    const err = event.error || {};
    reportClientDiagnosticError({
      service,
      errorName: err.name || 'UncaughtException',
      message: event.message || err.message || 'Uncaught error event',
      stack: err.stack,
      level: 'error',
      customDetails: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const isError = reason instanceof Error;
    reportClientDiagnosticError({
      service,
      errorName: isError ? reason.name : 'UnhandledRejection',
      message: isError ? reason.message : String(reason || 'Unhandled promise rejection'),
      stack: isError ? reason.stack : undefined,
      level: 'error',
      customDetails: {
        rejectionReason: isError ? undefined : reason,
      },
    });
  });
}

export function reportDataFetchError(
  service: 'dashboard' | 'crm',
  module: string,
  err: any,
  extra?: Record<string, any>
): void {
  if (!err) return;
  // Ignore request cancellations (e.g. typing debounces or unmounting)
  if (err.name === 'AbortError') return;
  // Ignore expected authentication denials/expirations
  if (err.status === 401 || err.status === 403 || err.statusCode === 401 || err.statusCode === 403) return;
  if (typeof err.message === 'string' && (err.message.includes('401') || err.message.includes('403') || err.message.includes('Unauthorized'))) return;

  reportClientDiagnosticError({
    service,
    errorName: err.name || `${module}Error`,
    message: err.message || `Unexpected error in ${module}`,
    stack: err.stack,
    level: 'error',
    customDetails: {
      module,
      ...extra
    }
  });
}

